import React, { useEffect, useState } from 'react';
import {
  AccordionSummary,
  Typography,
  AccordionDetails,
  Checkbox,
  useTheme,
  Stack,
  Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

import { Strings } from '@app/constants';
import { useDialog, useRequestState } from '@app/hooks';
import { Card404, Scrollbar, StatusTag } from '@app/components';
import { AddSchedule } from './add-schedule-popup';
import { getSchedule, updateScheduleStatus } from '../slice';
import { selectScheduleState, selectUpdateScheduleStatusState } from '../selecter';
import { ISchedule } from '../type';
import { StyledTitle } from '@app/modules/dashboard/style';

const ScheduleList = () => {
  const theme = useTheme();
  const addScheduleDialog = useDialog();
  const dispatch = useDispatch();

  const [checkedTasks, setCheckedTasks] = useState<Record<number, boolean>>({});

  const handleCheckboxChange = (taskId: number, status: boolean) => {
    setCheckedTasks((prevState) => ({
      ...prevState,
      [taskId]: !prevState[taskId],
    }));
    dispatch(updateScheduleStatus({ taskId, status: !status }));
  };

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    dispatch(getSchedule());
  }, [dispatch]);

  const { data } = useRequestState({
    stateSelector: selectScheduleState,
    errorShown: true,
  });

  useRequestState({
    stateSelector: selectUpdateScheduleStatusState,
    successMessageShown: true,
    errorShown: true,
  });

  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'} mt={1}>
        <StyledTitle> {Strings.dashboard.mySchedule}</StyledTitle>
        <Stack mr={'13px'} mt={'5px'}>
          <Button variant="contained" onClick={addScheduleDialog.show}>
            {Strings.button.addNew}
          </Button>
        </Stack>
      </Stack>

      <Stack sx={{ borderRadius: '5px', ml: 2, mr: 1, height: 'auto', mb: 1 }}>
        <Scrollbar sx={{ height: { md: 350 }, maxHeight: { xs: 350 } }}>
          {data?.length > 0 ? (
            data?.map((task: ISchedule) => {
              return (
                <Stack
                  key={task.id}
                  sx={{
                    borderBottom: `1.5px solid ${theme.palette.divider}`,
                    borderRadius: '10px',
                  }}
                >
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ArrowDropDownIcon />}
                      aria-controls="panel2-content"
                      id="panel2-header"
                      sx={{ pl: 1 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                            sx={{ mt: -1 }}
                            checked={checkedTasks[task.id] || task.status}
                            onChange={() => {
                              handleCheckboxChange(task.id, checkedTasks[task.id] || false);
                            }}
                            onClick={handleCheckboxClick}
                          />
                          <Stack spacing={0.2}>
                            <Typography
                              sx={{
                                textDecoration:
                                  checkedTasks[task.id] || task.status ? 'line-through' : 'none',
                                color:
                                  checkedTasks[task.id] || task.status
                                    ? theme.palette.grey[400]
                                    : 'inherit',
                              }}
                            >
                              {task.name}
                            </Typography>
                            <Typography
                              fontSize={10}
                              sx={{
                                color: checkedTasks[task.id]
                                  ? theme.palette.grey[400]
                                  : theme.palette.grey[500],
                              }}
                            >
                              {task.date}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Right side: Status */}
                        <StatusTag status={task.priority} />
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Typography mt={-2} fontSize={13}>
                        {task.description}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              );
            })
          ) : (
            <Card404 height={300} />
          )}
        </Scrollbar>
      </Stack>
      {addScheduleDialog.visible && <AddSchedule open onClose={addScheduleDialog.hide} />}
    </>
  );
};

export { ScheduleList };
